import React, { useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faSpinner, faLock } from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import { formatPrice } from '../../utils/currency';
import { createPayPalOrder, capturePayPalOrder } from './checkoutApi';
import GooglePayButton from './GooglePayButton';
import ApplePayButton from './ApplePayButton';
import './checkout.css';

// PayPal SDK ortamı backend ile aynı olmalı. client-id env'den (yoksa fallback).
const CLIENT_ID =
  process.env.REACT_APP_PAYPAL_CLIENT_ID ||
  'Ae_mz-6hAXMEAyQbS-Du_yNXY5Ggpq7oMgXvbDg7CsDjm7nT-26OC0sOimppI8OZcF60PDsw0bKXvTA7';
const CURRENCY = process.env.REACT_APP_PAYPAL_CURRENCY || 'EUR';

// NOT: PayPal SDK 'tr_TR' locale'inde buton etiketlerini boş render ediyor
// (bilinen sorun). Türkçe için en_US'a düşüyoruz.
const PP_LOCALES = { tr: 'en_US', en: 'en_US', de: 'de_DE' };

const Checkout = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state;
  const ppLocale = PP_LOCALES[(i18n.language || 'tr').split('-')[0]] || 'en_US';

  // SDK options'ı sabit referansta tut. buttons + applepay + googlepay bileşenleri.
  const ppOptions = useMemo(
    () => ({
      'client-id': CLIENT_ID,
      currency: CURRENCY,
      intent: 'capture',
      locale: ppLocale,
      components: 'buttons,applepay,googlepay',
      'disable-funding': 'paylater,credit',
    }),
    [ppLocale]
  );

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  // Callback closure'ları için güncel değerleri ref'ten oku (stale state önler).
  const latestRef = useRef({});

  if (!order?.productId || !order?.priceId) {
    return <Navigate to="/" replace />;
  }

  const { productId, priceId, plan = {} } = order;
  latestRef.current = { productId, priceId, plan };
  const validityText = /^\d+$/.test(String(plan.validity))
    ? t('detail.validity', { days: plan.validity })
    : plan.validity;
  const priceText = formatPrice(plan.priceEuro, 'EUR');
  const amount = (() => {
    const n = parseFloat(plan.priceEuro);
    return Number.isNaN(n) ? null : n.toFixed(2);
  })();

  // Capture sonucunu order success'e taşır. Tüm yöntemler (PayPal/Apple/Google)
  // ödeme onaylandıktan sonra bunu çağırır. Başarıda true döner.
  const finalizeOrder = (res) => {
    const cur = latestRef.current.plan || {};
    if (res?.success && res?.data) {
      navigate('/order/success', {
        replace: true,
        state: {
          qrCodeData: res.data.qrCodeData,
          plan: {
            countryName: res.data.purchasedPlan?.countryName || cur.countryName,
            dataAmount: res.data.purchasedPlan?.dataAmount || cur.dataAmount,
            validity: res.data.purchasedPlan?.validity || cur.validity,
            flag: cur.flag,
          },
        },
      });
      return true;
    }
    return false;
  };

  // Bir orderId'yi capture edip order success'e götürür (Apple/Google için ortak).
  const captureAndFinalize = async (orderId) => {
    setProcessing(true);
    setError(null);
    try {
      const res = await capturePayPalOrder(orderId);
      if (finalizeOrder(res)) return true;
      setError(res?.message || res?.error || t('checkout.error'));
    } catch (err) {
      setError(err.message === 'processing-timeout' ? t('checkout.processing') : t('checkout.error'));
    }
    setProcessing(false);
    return false;
  };

  const handleApprove = (data) => captureAndFinalize(data.orderID);

  const createOrder = () => {
    const cur = latestRef.current;
    return createPayPalOrder({ productId: cur.productId, priceId: cur.priceId });
  };

  const walletProps = {
    amount,
    currency: CURRENCY,
    label: plan.countryName,
    createOrder,
    captureAndFinalize,
    onError: setError,
    onBusy: setProcessing,
    disabled: processing,
  };

  return (
    <div className="checkout">
      <StoreHeader />

      <main className="checkout-main">
        <h1 className="checkout-title">{t('checkout.title')}</h1>

        {/* Sipariş özeti */}
        <section className="checkout-summary">
          <h2 className="checkout-summary-title">{t('checkout.summary')}</h2>
          <div className="checkout-line">
            {plan.flag ? (
              <img className="checkout-flag" src={plan.flag} alt="" />
            ) : (
              <span className="checkout-flag checkout-flag--fallback">
                <FontAwesomeIcon icon={faGlobe} />
              </span>
            )}
            <div className="checkout-line-info">
              <span className="checkout-line-name">{plan.countryName}</span>
              <span className="checkout-line-meta">
                {plan.dataAmount}
                {validityText ? ` · ${validityText}` : ''}
              </span>
            </div>
            {priceText && <span className="checkout-line-price">{priceText}</span>}
          </div>

          <div className="checkout-total">
            <span>{t('checkout.total')}</span>
            <strong>{priceText}</strong>
          </div>
          <p className="checkout-eur-note">{t('checkout.eurNote')}</p>
        </section>

        {/* Ödeme */}
        <section className="checkout-pay">
          {error && <div className="checkout-error">{error}</div>}

          {processing && (
            <div className="checkout-processing">
              <FontAwesomeIcon icon={faSpinner} spin />
              {t('checkout.processing')}
            </div>
          )}

          <div className={`checkout-buttons ${processing ? 'is-busy' : ''}`}>
            <PayPalScriptProvider options={ppOptions}>
              {/* Apple Pay / Google Pay (uygunsa kendileri görünür) */}
              <ApplePayButton {...walletProps} />
              <GooglePayButton {...walletProps} />

              <PayPalButtons
                style={{ layout: 'vertical', shape: 'rect', color: 'blue' }}
                forceReRender={[productId, priceId, ppLocale]}
                disabled={processing}
                createOrder={async () => {
                  setError(null);
                  const res = await createOrder();
                  return res.orderId;
                }}
                onApprove={handleApprove}
                onCancel={() => setError(t('checkout.cancelled'))}
                onError={() => setError(t('checkout.error'))}
              />
            </PayPalScriptProvider>
          </div>

          <p className="checkout-secure">
            <FontAwesomeIcon icon={faLock} />
            PayPal
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Checkout;
